import { EventEmitter, Injectable } from "@angular/core";
import { isAddin } from "../../utilities/soAuthenticatorUtils";
import { IPreferencesDialogstate } from "../../model/dialog-state.model";
import { ICapabilitiesPrivileged, CustomPropertyKey } from "../../model/shared.model";
import { observableOfOutlookAsync, addAsync, setAsync } from "./async.helpers";

@Injectable({
  providedIn: "root"
})
export class OutlookService {
  private customPropertiesQueue: Promise<void> = Promise.resolve();

  constructor() {

  }

  get mailboxItem() {
    return Office.context.mailbox.item;
  }

  get mailbox() {
    return Office.context.mailbox;
  }

  get officeEventType(): typeof Office.EventType {
    return Office.EventType;
  }

  get internetMessageId(): string {
    return this.mailboxItem.internetMessageId;
  }

  get displayLanguage() {
    let language = Office.context.displayLanguage;
    if (this.mailbox.diagnostics.hostName === "OutlookIOS") {
      language = navigator.language; // preferred language of the user, usually the language of the browser UI
    }

    switch (language.toLowerCase()) {
      case "nb-no": // Office for the web - Norwegian (Bokmål)
      case "nn-no": // Office for the web - Norwegian (Nynorsk)
      case "nb":    // iOS & Android Supported Language Codes - ISO 639-1 Language Code
      case "nn":    // iOS & Android Supported Language Codes - ISO 639-1 Language Code
      case "nob":   // iOS & Android Supported Language Codes - ISO 639-2 Language Code
      case "nno":   // iOS & Android Supported Language Codes - ISO 639-2 Language Code
      case "nor":   // iOS & Android Supported Language Codes - ISO 639-2 Language Code
        return "NO";
    }

    return Office.context.displayLanguage;
  }

  get isRequirementSet18Supported() {
    return Office.context.requirements.isSetSupported("Mailbox", "1.8");
  }

  get isMobile(): boolean {
    return Office.context.platform === Office.PlatformType.iOS
      || Office.context.platform === Office.PlatformType.Android;
  }

  get supportsAttachmentContent(): boolean {
    if (this.isMobile) return false;
    return typeof this.mailboxItem?.getAttachmentContentAsync === "function"
      && Office.context.requirements.isSetSupported("Mailbox", "1.8");
  }

  get isRequirementSet16Supported() {
    return Office.context.requirements.isSetSupported("Mailbox", "1.6");
  }

  get supportsSessionData(): boolean {
    return Office.context.requirements.isSetSupported("Mailbox", "1.11");
  }

  get ui() {
    return Office.context.ui;
  }

  get roamingSettings() {
    return Office.context.roamingSettings;
  }

  /** Resolves to the raw initialization context string, or null if absent or the call failed. */
  getInitializationContext(): Promise<string | null> {
    return new Promise((resolve) => {
      this.mailboxItem.getInitializationContextAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
          resolve(result.value);
        } else {
          resolve(null);
        }
      });
    });
  }

  /** Resolves to the item's custom properties bag, or rejects on failure. */
  loadCustomProperties(item = this.mailboxItem): Promise<Office.CustomProperties> {
    return new Promise((resolve, reject) => {
      item.loadCustomPropertiesAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      });
    });
  }

  updateCustomProperty(key: CustomPropertyKey, value: string): Promise<void> {
    return this.updateCustomProperties({ [key]: value });
  }

  // Session copy of a custom property, read by launchevent.js when the property is missing at send time.
  updateSessionData(key: CustomPropertyKey, value: string): Promise<void> {
    if (!this.supportsSessionData) {
      return Promise.resolve();
    }
    const item = this.mailboxItem;
    return new Promise<void>((resolve, reject) => {
      item.sessionData.setAsync(key, value, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(result.error);
        }
      });
    });
  }

  // Each save writes the whole property bag, so related keys go in one call.
  // Calls run in sequence to not overwrite each other, and keep the item they were made for.
  updateCustomProperties(entries: Record<string, string>): Promise<void> {
    const item = this.mailboxItem;
    const run = this.customPropertiesQueue.then(() => this.saveCustomProperties(item, entries));
    this.customPropertiesQueue = run.catch(() => undefined);
    return run;
  }

  private async saveCustomProperties(item: OutlookService["mailboxItem"], entries: Record<string, string>): Promise<void> {
    const props = await this.loadCustomProperties(item);
    for (const [key, value] of Object.entries(entries)) {
      props.set(key, value);
    }
    return new Promise<void>((resolve, reject) => {
      props.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(result.error);
        }
      });
    });
  }

  get isAddin() {
    return isAddin();
  }

  get mailboxItemTypeAppointment(): OutlookItemType {
    return "appointment"
  }

  get mailboxItemTypeMessage(): OutlookItemType {
    return "message";
  }

  convertToRestId(itemId: string) {
    return this.mailbox.convertToRestId(itemId, Office.MailboxEnums.RestVersion.v2_0);
  }

  get isAppointment(): boolean {
    return this.mailboxItem.itemType === this.mailboxItemTypeAppointment;
  }

  get itemId() {
    return this.mailboxItem?.itemId ?? null;
  }

  // itemId is EWS-formatted on every host except OutlookIOS/OutlookAndroid, where it's already
  // REST-formatted and convertToRestId is unsupported (mirrors SharedService.getItemRestId(),
  // which applies the same split for messages).
  get restItemId(): string | null {
    const itemId = this.itemId;
    if (!itemId) {
      return null;
    }
    const hostName = this.mailbox.diagnostics.hostName;
    if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
      return itemId;
    }
    return this.convertToRestId(itemId);
  }

  get organizer() {
    return observableOfOutlookAsync(this.mailboxItem.organizer);
  }

  get requiredAttendees() {
    return observableOfOutlookAsync(this.mailboxItem.requiredAttendees);
  }

  get optionalAttendees() {
    return observableOfOutlookAsync(this.mailboxItem.optionalAttendees)
  }

  get userProfile() {
    return this.mailbox.userProfile;
  }

  get attachments(): Office.AttachmentDetails[] {
    return this.mailboxItem?.attachments ?? [];
  }

  getAttachmentContent(attachmentId: string): Promise<Office.AttachmentContent> {
    return new Promise((resolve, reject) => {
      this.mailboxItem.getAttachmentContentAsync(attachmentId, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(result.error);
        }
      });
    });
  }

  get to() {
    return observableOfOutlookAsync(this.mailboxItem.to);
  }

  get bcc() {
    return observableOfOutlookAsync(this.mailboxItem.bcc);
  }

  get cc() {
    return observableOfOutlookAsync(this.mailboxItem.cc);
  }

  async setRecipient(type: string, value: Office.EmailAddressDetails[]): Promise<void> {
    await setAsync(value, this.mailboxItem[type]);
  }

  async addRecipient(type: string, value: Office.EmailAddressDetails[]): Promise<void> {
    await addAsync(value, this.mailboxItem[type]);
  }

  public currentPreferencesDialogState: IPreferencesDialogstate;

  public currentCapabilitiesPrivileged: ICapabilitiesPrivileged[] = [];

  // Custom event
  public itemChangedEvent = new EventEmitter<any>();

  private dialog: Office.Dialog | null = null;
  private maxRetries = 5;
  private retryDelay = 300;

  public openDialog(
    url: string,
    width: number,
    height: number,
    onMessageReceived: (message: string) => void,
    onError?: (error: any) => void,
    onLogMessage?: (logMessage: string, error: any | null) => void
  ) {
    let retryCount = 0;

    const openNewDialog = () => {
      Office.context.ui.displayDialogAsync(url, { width, height }, (result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          onError?.(result.error);
          return;
        }

        this.dialog = result.value;

        const safeClose = () => {
          if (this.dialog) {
            try {
              this.dialog.close();
            } catch (e) {
              onLogMessage?.(`FAILED: close dialog`, e);
            } finally {
              this.dialog = null;
            }
          }
        };

        this.dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: any) => {
          try {
            if ("message" in args) {
              onMessageReceived(args.message as string);
            } else {
              onError?.(args);
            }
          } catch (e) {
            onError?.(e);
          } finally {
            safeClose();
          }
        });

        this.dialog.addEventHandler(Office.EventType.DialogEventReceived, (args: any) => {
          onError?.(args);
          safeClose();
        });
      });
    };

    const tryOpenDialog = () => {
      if (!this.dialog) {
        openNewDialog();
      } else if (retryCount < this.maxRetries) {
        retryCount++;
        setTimeout(tryOpenDialog, this.retryDelay);
      } else {
        onError?.(new Error("Max retries reached. Dialog could not be opened."));
      }
    };

    tryOpenDialog();
  }

  saveRoamingSettings(entries: Record<string, boolean | string | number>): Promise<void> {
    for (const [key, value] of Object.entries(entries)) {
      Office.context.roamingSettings.set(key, value);
    }
    return new Promise((resolve) =>
      Office.context.roamingSettings.saveAsync(() => resolve())
    );
  }

  getRoamingSettingBoolean(key: string, defaultValue = false): boolean {
    const value = Office.context.roamingSettings.get(key);
    return (value === undefined || value === null) ? defaultValue : value === true;
  }

  getRoamingSettingString(key: string, defaultValue = ""): string {
    const value = Office.context.roamingSettings.get(key);
    return (value === undefined || value === null) ? defaultValue : String(value);
  }
}

export type OutlookItemType = "message" | "appointment";
